import React, { useEffect, useMemo, useState } from "react";
import api, { API, formatApiErrorDetail, getToken } from "../lib/api";
import { PiggyBank, UploadCloud, Trash2, Download, Loader2 } from "lucide-react";

const inp = "w-full h-11 px-3 border border-stone-300 text-sm bg-white";
const DOC_TYPES = ["Annual Budget", "Budget vs Actual"];

export default function Budgets() {
  const [docs, setDocs] = useState(null);
  const [ranches, setRanches] = useState([]);
  const [f, setF] = useState({ ranch_number: "", title: "", doc_type: "Budget vs Actual", period: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const [b, r] = await Promise.all([api.get("/budgets"), api.get("/ranches")]);
    setDocs(b.data); setRanches(r.data);
  };
  useEffect(() => { load(); }, []);

  const vineyards = useMemo(() => [...new Map(ranches.map((r) => [r.ranch_number, r])).values()], [ranches]);

  const upload = async () => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ranch_number", f.ranch_number);
      fd.append("title", f.title);
      fd.append("doc_type", f.doc_type);
      if (f.period) fd.append("period", f.period);
      await api.post("/budgets", fd);
      setF({ ranch_number: "", title: "", doc_type: "Budget vs Actual", period: "" });
      setFile(null);
      document.getElementById("budget-file-input").value = "";
      await load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    await api.delete(`/budgets/${d.id}`);
    await load();
  };

  const download = async (d) => {
    const token = getToken();
    const res = await fetch(`${API}/budgets/${d.id}/file`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = d.filename || d.title; a.click();
    URL.revokeObjectURL(url);
  };

  if (!docs) return <div className="p-8 font-bold uppercase text-sm">Loading budgets…</div>;

  return (
    <div className="space-y-5" data-testid="budgets-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Budgets</h1>
        <p className="text-stone-500 text-sm mt-1">Upload annual budgets and monthly budget-vs-actual reports — clients see documents for their ranches in the portal.</p>
      </div>

      <div className="bg-white border border-stone-200 p-4 space-y-3" data-testid="budget-upload-form">
        <div className="font-bold uppercase tracking-wide text-sm flex items-center gap-2"><UploadCloud size={15} /> Upload Document</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">Ranch</div>
            <select data-testid="budget-ranch-select" value={f.ranch_number} onChange={(e) => setF({ ...f, ranch_number: e.target.value })} className={inp}>
              <option value="">— Select Ranch —</option>
              {vineyards.map((r) => <option key={r.ranch_number} value={r.ranch_number}>{r.ranch_number} · {r.name}</option>)}
            </select></div>
          <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">Title</div>
            <input data-testid="budget-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} placeholder="e.g. 2026 Farming Budget" /></div>
          <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">Type</div>
            <select data-testid="budget-type-select" value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} className={inp}>
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">Period (optional)</div>
            <input data-testid="budget-period-input" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} className={inp} placeholder="e.g. 2026 or 2026-06" /></div>
        </div>
        <input id="budget-file-input" data-testid="budget-file-input" type="file" accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg"
          onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
        {err && <div className="bg-red-50 border border-red-400 text-red-800 p-3 text-sm font-semibold" data-testid="budget-error">{err}</div>}
        <button data-testid="budget-upload-btn" onClick={upload} disabled={busy || !file || !f.ranch_number || !f.title.trim()}
          className="h-11 px-5 bg-[#1F3D34] text-white font-bold uppercase text-xs disabled:opacity-40 flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload
        </button>
      </div>

      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.id} data-testid={`budget-doc-${d.id}`} className="bg-white border border-stone-200 p-4 flex flex-wrap items-center gap-3">
            <PiggyBank size={18} className="text-[#7C9673] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold">{d.title} <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-stone-100 border border-stone-200 ml-1">{d.doc_type}</span></div>
              <div className="text-xs text-stone-500">Ranch {d.ranch_number}{d.period ? ` · ${d.period}` : ""} · {d.filename} · uploaded {d.created_at?.slice(0, 10)} by {d.uploaded_by}</div>
            </div>
            <button data-testid={`budget-download-${d.id}`} onClick={() => download(d)} className="h-10 w-10 border border-stone-300 flex items-center justify-center"><Download size={14} /></button>
            <button data-testid={`budget-delete-${d.id}`} onClick={() => remove(d)} className="h-10 w-10 border border-stone-300 flex items-center justify-center text-red-700"><Trash2 size={14} /></button>
          </div>
        ))}
        {docs.length === 0 && (
          <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm" data-testid="budgets-empty">No budget documents uploaded yet</div>
        )}
      </div>
    </div>
  );
}
